import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  IconButton,
  Skeleton,
  Snackbar,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import RefreshIcon from "@mui/icons-material/Refresh";

import { hasPermission } from "../../helpers/selectors";
import { formatIso, formatRelative } from "../../helpers/date";
import {
  exportSupportAuditCsv,
  getMe,
  listSupportAudit,
  listSupportAuditActions,
  putSupportAuditSubscribe,
} from "./api";

/**
 * SCK-569 — Layer 3 (Customer support-audit console) page.
 *
 * <p>Renders the ACCOUNT_ADMIN's view of every support-access grant
 * their tenant has ever received. Each row is one audit-worthy grant
 * (Active | Completed | Denied | Expired — PENDING lives on the
 * approval banner, not here). Row expand drops in the per-session
 * action list from GET /api/support-audit/{id}/actions; the
 * subscription toggle round-trips through
 * PUT /api/support-audit/subscribe; the Export button pulls a
 * text/csv attachment for SOC 2 evidence.
 *
 * <p>Auth: rendered inside AccessLayout which is wrapped in
 * ProtectedRoutes(SYSTEM | ACCOUNT_ADMIN). This page still guards
 * with hasPermission for the same reason AccessInvitations does —
 * belt-and-suspenders so a route regression can't leak the page.
 */

const STATUS_LABELS = ["All", "Active", "Completed", "Denied", "Expired"];
const STATUS_COLORS = {
  Active: "success",
  Completed: "default",
  Denied: "error",
  Expired: "warning",
};

function statusChipVariant(s) {
  return s === "Active" ? "filled" : "outlined";
}

function bufferToBlobDownload(text, filename) {
  try {
    const blob = new Blob([text], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    // Revoke on a microtask so the click has time to consume the URL.
    setTimeout(() => URL.revokeObjectURL(url), 0);
    return true;
  } catch (e) {
    // Some sandboxed environments block programmatic downloads. Surface
    // a toast rather than a silent no-op.
    return false;
  }
}

export default function SupportAudit({ currentUser }) {
  const theme = useTheme();
  const canView = hasPermission(currentUser, ["SYSTEM", "ACCOUNT_ADMIN"]);

  const [accountId, setAccountId] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [status, setStatus] = useState("All");
  const [engineerFilter, setEngineerFilter] = useState("");
  const [pageStart, setPageStart] = useState("");
  const [pageEnd, setPageEnd] = useState("");
  const [expandedRow, setExpandedRow] = useState(null);
  const [actionCache, setActionCache] = useState({}); // { sessionId: { rows, loading, error } }
  const [subscribed, setSubscribed] = useState(false);
  const [subscribePending, setSubscribePending] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [toast, setToast] = useState(null);

  const filters = useMemo(
    () => ({
      accountId,
      status,
      engineerId: engineerFilter?.trim() || undefined,
      pageStart: pageStart ? new Date(pageStart).toISOString() : undefined,
      pageEnd: pageEnd ? new Date(pageEnd).toISOString() : undefined,
    }),
    [accountId, status, engineerFilter, pageStart, pageEnd]
  );

  const refresh = useCallback(async () => {
    if (!canView) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const self = accountId ? { id: accountId } : await getMe();
      if (!accountId) setAccountId(self.id);
      const payload = await listSupportAudit({
        accountId: self.id,
        status,
        engineerId: engineerFilter?.trim() || undefined,
        pageStart: pageStart ? new Date(pageStart).toISOString() : undefined,
        pageEnd: pageEnd ? new Date(pageEnd).toISOString() : undefined,
      });
      setRows(Array.isArray(payload) ? payload : []);
    } catch (e) {
      setError(e);
    } finally {
      setLoading(false);
    }
  }, [canView, accountId, status, engineerFilter, pageStart, pageEnd]);

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // Re-fetch whenever a filter changes (skip the very first mount,
    // handled above).
    if (accountId) refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, engineerFilter, pageStart, pageEnd]);

  const loadActionsFor = useCallback(
    async (row) => {
      if (!row?.sessionId) {
        setActionCache((prev) => ({
          ...prev,
          [row.requestId]: { rows: [], loading: false, error: null },
        }));
        return;
      }
      setActionCache((prev) => ({
        ...prev,
        [row.sessionId]: { rows: [], loading: true, error: null },
      }));
      try {
        const list = await listSupportAuditActions(row.sessionId, {
          accountId,
        });
        setActionCache((prev) => ({
          ...prev,
          [row.sessionId]: {
            rows: Array.isArray(list) ? list : [],
            loading: false,
            error: null,
          },
        }));
      } catch (e) {
        setActionCache((prev) => ({
          ...prev,
          [row.sessionId]: { rows: [], loading: false, error: e },
        }));
      }
    },
    [accountId]
  );

  const toggleRow = (row) => {
    const key = row.sessionId || row.requestId;
    if (expandedRow === key) {
      setExpandedRow(null);
      return;
    }
    setExpandedRow(key);
    // Cache is per-session; only re-fetch on first expand or on refresh.
    if (row.sessionId && !actionCache[row.sessionId]) {
      loadActionsFor(row);
    }
  };

  const onSubscribeToggle = async (nextValue) => {
    setSubscribePending(true);
    try {
      const res = await putSupportAuditSubscribe(nextValue);
      setSubscribed(!!res?.onNewSession);
      setToast({
        severity: "success",
        message: nextValue
          ? "You will be notified on new support access."
          : "Notifications for new support access turned off.",
      });
    } catch (e) {
      setToast({
        severity: "error",
        message: `Couldn't update subscription: ${e.message || e}`,
      });
    } finally {
      setSubscribePending(false);
    }
  };

  const onExport = async () => {
    setExporting(true);
    try {
      const csv = await exportSupportAuditCsv(filters);
      const stamp = new Date().toISOString().slice(0, 10);
      const ok = bufferToBlobDownload(csv, `support-audit-${stamp}.csv`);
      if (!ok) {
        setToast({
          severity: "warning",
          message:
            "Couldn't trigger the download. Copy the URL from network tools if you need the CSV.",
        });
      }
    } catch (e) {
      setToast({
        severity: "error",
        message: `Export failed: ${e.message || e}`,
      });
    } finally {
      setExporting(false);
    }
  };

  if (!canView || (error && error.status === 403)) {
    return (
      <Alert
        severity="warning"
        icon={<LockOutlinedIcon fontSize="small" />}
        sx={{ mt: 2 }}
        data-testid="support-audit-forbidden"
      >
        Only account admins can view support audit history. Ask your
        account owner if you need access.
      </Alert>
    );
  }

  const filtered = rows; // server-side filtered
  const empty = !loading && !error && filtered.length === 0;

  return (
    <Box data-testid="support-audit-page">
      {/* Toolbar row: filters + subscribe + export */}
      <Stack
        direction={{ xs: "column", md: "row" }}
        spacing={2}
        alignItems={{ md: "flex-end" }}
        justifyContent="space-between"
        sx={{ mb: 2 }}
      >
        <Stack direction="row" spacing={1} flexWrap="wrap" rowGap={1}>
          {STATUS_LABELS.map((s) => (
            <Chip
              key={s}
              label={s}
              size="small"
              color={status === s ? "primary" : "default"}
              variant={status === s ? "filled" : "outlined"}
              onClick={() => setStatus(s)}
              data-testid={`support-audit-filter-${s.toLowerCase()}`}
              sx={{ fontWeight: 600 }}
            />
          ))}
          <TextField
            size="small"
            label="Engineer id"
            value={engineerFilter}
            onChange={(e) => setEngineerFilter(e.target.value)}
            sx={{ width: 220 }}
            inputProps={{ "data-testid": "support-audit-filter-engineer" }}
          />
          <TextField
            size="small"
            type="datetime-local"
            label="From"
            value={pageStart}
            onChange={(e) => setPageStart(e.target.value)}
            InputLabelProps={{ shrink: true }}
            inputProps={{ "data-testid": "support-audit-filter-start" }}
            sx={{ width: 200 }}
          />
          <TextField
            size="small"
            type="datetime-local"
            label="To"
            value={pageEnd}
            onChange={(e) => setPageEnd(e.target.value)}
            InputLabelProps={{ shrink: true }}
            inputProps={{ "data-testid": "support-audit-filter-end" }}
            sx={{ width: 200 }}
          />
        </Stack>
        <Stack direction="row" spacing={2} alignItems="center">
          <Tooltip title="Send me a notification when a new support session begins on this account.">
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <Switch
                size="small"
                checked={subscribed}
                onChange={(e) => onSubscribeToggle(e.target.checked)}
                disabled={subscribePending}
                inputProps={{
                  "aria-label": "Notify me on new support access",
                  "data-testid": "support-audit-subscribe",
                }}
              />
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                Notify me on new support access
              </Typography>
            </Stack>
          </Tooltip>
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={onExport}
            disabled={exporting || loading}
            data-testid="support-audit-export"
            sx={{ textTransform: "none" }}
          >
            {exporting ? "Exporting…" : "Export CSV"}
          </Button>
        </Stack>
      </Stack>

      {error && error.status !== 403 && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          action={
            <Button color="inherit" size="small" onClick={refresh}>
              Retry
            </Button>
          }
          data-testid="support-audit-error"
        >
          Couldn&apos;t load support audit. {(error.message || "").slice(0, 200)}
        </Alert>
      )}

      <TableContainer
        sx={{
          border: 1,
          borderColor: "divider",
          borderRadius: 1,
          background:
            theme.palette.mode === "dark"
              ? "rgba(255,255,255,0.02)"
              : "rgba(0,0,0,0.01)",
        }}
      >
        <Table
          size="small"
          sx={{ minWidth: 900 }}
          aria-label="Support access audit history"
        >
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: 40 }} />
              <TableCell>Engineer</TableCell>
              <TableCell>Reason</TableCell>
              <TableCell>Start</TableCell>
              <TableCell>End</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading &&
              Array.from({ length: 3 }).map((_, i) => (
                <TableRow key={`sk-${i}`}>
                  {Array.from({ length: 7 }).map((__, j) => (
                    <TableCell key={j}>
                      <Skeleton
                        variant="rectangular"
                        height={22}
                        sx={{ borderRadius: 0.5 }}
                      />
                    </TableCell>
                  ))}
                </TableRow>
              ))}

            {empty && (
              <TableRow>
                <TableCell colSpan={7}>
                  <Typography
                    variant="body2"
                    sx={{
                      py: 6,
                      textAlign: "center",
                      color: "text.secondary",
                    }}
                    data-testid="support-audit-empty"
                  >
                    No support-access sessions have been requested for this
                    account yet.
                  </Typography>
                </TableCell>
              </TableRow>
            )}

            {!loading &&
              filtered.map((row) => {
                const key = row.sessionId || row.requestId;
                const isOpen = expandedRow === key;
                const start = row.startAt || row.approvedAt || row.createdAt;
                const end = row.endAt;
                return (
                  <React.Fragment key={key}>
                    <TableRow
                      hover
                      data-testid="support-audit-row"
                    >
                      <TableCell>
                        <IconButton
                          size="small"
                          onClick={() => toggleRow(row)}
                          aria-label={isOpen ? "Collapse row" : "Expand row"}
                          data-testid="support-audit-row-toggle"
                        >
                          {isOpen ? (
                            <ExpandLessIcon fontSize="small" />
                          ) : (
                            <ExpandMoreIcon fontSize="small" />
                          )}
                        </IconButton>
                      </TableCell>
                      <TableCell>
                        <Typography sx={{ fontWeight: 600 }}>
                          {row.engineerEmail || row.engineerName || "—"}
                        </Typography>
                        {row.engineerName && row.engineerEmail && (
                          <Typography
                            variant="caption"
                            sx={{ color: "text.secondary" }}
                          >
                            {row.engineerName}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Tooltip title={row.reason || ""}>
                          <span
                            style={{
                              display: "inline-block",
                              maxWidth: 320,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              verticalAlign: "bottom",
                            }}
                          >
                            {row.reason || "—"}
                          </span>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        <Tooltip title={formatIso(start) || ""}>
                          <span
                            style={{
                              fontSize: 13,
                              color: theme.palette.text.secondary,
                            }}
                          >
                            {formatRelative(start) || "—"}
                          </span>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        <Tooltip title={formatIso(end) || ""}>
                          <span
                            style={{
                              fontSize: 13,
                              color: theme.palette.text.secondary,
                            }}
                          >
                            {formatRelative(end) || "—"}
                          </span>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={row.status || "—"}
                          size="small"
                          color={STATUS_COLORS[row.status] || "default"}
                          variant={statusChipVariant(row.status)}
                          sx={{
                            height: 22,
                            fontSize: 11.5,
                            fontWeight: 600,
                          }}
                          data-testid={`support-audit-status-${
                            row.status || "unknown"
                          }`}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <span
                          style={{
                            fontVariantNumeric: "tabular-nums",
                            fontSize: 13,
                          }}
                          data-testid="support-audit-action-count"
                        >
                          {row.actionCount ?? 0}
                        </span>
                      </TableCell>
                    </TableRow>
                    {isOpen && (
                      <TableRow data-testid="support-audit-actions-row">
                        <TableCell />
                        <TableCell colSpan={6} sx={{ py: 2 }}>
                          <ActionsPanel
                            row={row}
                            state={actionCache[row.sessionId]}
                            onRetry={() => loadActionsFor(row)}
                          />
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                );
              })}
          </TableBody>
        </Table>
      </TableContainer>

      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ mt: 1.5 }}
      >
        <Typography variant="caption" sx={{ color: "text.secondary" }}>
          {loading
            ? "Loading support audit…"
            : `${filtered.length} session${filtered.length === 1 ? "" : "s"}`}
        </Typography>
        <Tooltip title="Refresh">
          <IconButton
            size="small"
            onClick={refresh}
            aria-label="Refresh support audit"
            data-testid="support-audit-refresh"
          >
            <RefreshIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Stack>

      <Snackbar
        open={!!toast}
        autoHideDuration={5000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        {toast ? (
          <Alert
            severity={toast.severity || "info"}
            onClose={() => setToast(null)}
            sx={{ width: "100%" }}
            data-testid="support-audit-toast"
          >
            {toast.message}
          </Alert>
        ) : undefined}
      </Snackbar>
    </Box>
  );
}

// ---------------- subcomponents ----------------

function ActionsPanel({ row, state, onRetry }) {
  if (!row?.sessionId) {
    return (
      <Typography
        variant="body2"
        sx={{ color: "text.secondary" }}
        data-testid="support-audit-actions-nosession"
      >
        No session was minted for this request (status: {row?.status || "—"}
        ), so there are no recorded actions.
      </Typography>
    );
  }
  if (!state || state.loading) {
    return (
      <Stack spacing={1}>
        {Array.from({ length: 2 }).map((_, i) => (
          <Skeleton key={i} variant="rectangular" height={18} />
        ))}
      </Stack>
    );
  }
  if (state.error) {
    return (
      <Alert
        severity="error"
        action={
          <Button color="inherit" size="small" onClick={onRetry}>
            Retry
          </Button>
        }
      >
        Couldn&apos;t load actions. {(state.error.message || "").slice(0, 200)}
      </Alert>
    );
  }
  const list = state.rows || [];
  if (list.length === 0) {
    return (
      <Typography
        variant="body2"
        sx={{ color: "text.secondary" }}
        data-testid="support-audit-actions-empty"
      >
        No recorded actions yet.
      </Typography>
    );
  }
  return (
    <Box>
      <Table size="small" aria-label="Session actions">
        <TableHead>
          <TableRow>
            <TableCell>When</TableCell>
            <TableCell>Method</TableCell>
            <TableCell>Endpoint</TableCell>
            <TableCell>Request id</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {list.map((a, i) => (
            <TableRow key={`${a.timestamp}-${i}`} data-testid="support-audit-action-row">
              <TableCell>
                <Tooltip title={formatIso(a.timestamp) || ""}>
                  <span>{formatRelative(a.timestamp) || "—"}</span>
                </Tooltip>
              </TableCell>
              <TableCell>
                <Chip
                  label={a.method || "—"}
                  size="small"
                  variant="outlined"
                  sx={{ height: 20, fontSize: 11 }}
                />
              </TableCell>
              <TableCell>
                <code style={{ fontSize: 12 }}>{a.endpoint || "—"}</code>
              </TableCell>
              <TableCell>
                <code style={{ fontSize: 12, color: "gray" }}>
                  {a.requestId || "—"}
                </code>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
}
